import { TrashIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import type { RecurringGroupKey, RecurringPayment } from '@/lib/api';
import { hasConvertedAmount } from '@/lib/recurring/money';
import {
  recurringStatusDate,
  type RecurringPaymentStatusView,
} from '@/lib/recurring/paymentStatus';
import { cn } from '@/lib/utils';
import { canDeleteRecurringPayment } from './actions';
import { ConnectorDot, type ConnectorBranding } from './ConnectorDot';
import { InstallmentTrack } from './InstallmentTrack';
import { RecurringAmount } from './RecurringAmount';
import { RecurringRowAvatar } from './RecurringRowAvatar';
import { RecurringStatusChip } from './RecurringStatusChip';
import { RECURRING_TAXONOMY } from './taxonomy';

export interface RecurringCommitment {
  item: RecurringPayment;
  groupKey: RecurringGroupKey;
  status: RecurringPaymentStatusView;
}

interface Props {
  commitment: RecurringCommitment;
  connectorFor?: (accountId: string | null) => ConnectorBranding | null;
  onOpenItem?: (item: RecurringPayment) => void;
  onDeleteRequest?: (item: RecurringPayment) => void;
}

function displayName(item: RecurringPayment): string {
  return item.merchantName ?? item.categoryTranslated ?? item.category ?? 'Recorrente';
}

function categoryLabel(item: RecurringPayment): string | null {
  return item.categoryTranslated ?? item.category ?? null;
}

function dateLabel(iso: string | null): string | null {
  if (!iso) return null;
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  // Local date from the parts so a midnight-UTC value can't slip a day.
  return new Date(year, month - 1, day)
    .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
    .replace(/\.$/, '');
}

/** "vence 18 ago" / "venceu 5 ago" while it is still owed, plain "14 ago" once the charge landed. */
function whenLabel(commitment: RecurringCommitment): string | null {
  const date = recurringStatusDate(commitment.item, commitment.status);
  const label = date && dateLabel(date.iso);
  if (!date || !label) return null;
  const owed = commitment.status === 'prevista' || commitment.status === 'atrasada';
  if (!owed) return label;
  return date.tense === 'past' ? `venceu ${label}` : `vence ${label}`;
}

/** One commitment, whatever its kind — the three taxonomies share this skeleton. */
export function RecurringCommitmentRow({
  commitment,
  connectorFor,
  onOpenItem,
  onDeleteRequest,
}: Props) {
  const formatCurrency = useFormatCurrency();
  const { item, groupKey, status } = commitment;
  const name = displayName(item);
  const category = categoryLabel(item);
  const branding = connectorFor?.(item.accountId);
  const when = whenLabel(commitment);
  const taxonomy = RECURRING_TAXONOMY[groupKey];
  const TaxonomyIcon = taxonomy.icon;
  const installmentTotal = item.installmentTotal ?? 0;

  const openItem = () => onOpenItem?.(item);
  const requestDelete = () => onDeleteRequest?.(item);

  const body = (
    <>
      <RecurringRowAvatar
        categoryKey={item.categoryId ?? item.category ?? 'outros'}
        label={name}
        iconName={item.categoryIcon ?? item.parentCategoryIcon ?? null}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="flex min-w-0 items-center gap-1.5">
              <span className="truncate text-sm leading-tight font-medium text-text-primary">{name}</span>
              <CurrencyBadge item={item} />
            </p>
            <p className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 text-2xs text-text-secondary">
              {category && <span className="truncate">{category}</span>}
              {item.dayOfMonth && (
                <>
                  {category && <span className="text-text-tertiary">·</span>}
                  <span>dia {item.dayOfMonth}</span>
                </>
              )}
              {branding && (
                <>
                  <span className="hidden text-text-tertiary xs:inline">·</span>
                  <span className="hidden min-w-0 items-center gap-1.5 xs:flex">
                    <ConnectorDot branding={branding} />
                    <span className="truncate">{branding.label}</span>
                  </span>
                </>
              )}
            </p>
          </div>
          <RecurringAmount item={item} />
        </div>

        {installmentTotal > 0 && (
          <InstallmentTrack billed={item.installmentCurrent ?? 0} total={installmentTotal} className="mt-2" />
        )}

        <div className="mt-1.5 flex items-center gap-2">
          <RecurringStatusChip status={status} size="sm" />
          {when && <span className="text-3xs text-text-tertiary">{when}</span>}
          <RemainingLabel item={item} formatCurrency={formatCurrency} />
          <span className="ml-auto hidden shrink-0 items-center gap-1 text-3xs text-text-tertiary sm:inline-flex">
            <TaxonomyIcon size={11} weight="bold" aria-hidden />
            {taxonomy.label}
          </span>
        </div>
      </div>
    </>
  );

  return (
    <li className="group flex items-center gap-1">
      {onOpenItem ? (
        <button
          type="button"
          onClick={openItem}
          className="flex min-w-0 flex-1 items-start gap-3 rounded-[var(--radius-md)] px-2 py-2.5 text-left transition-colors hover:bg-surface-sunken"
        >
          {body}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-start gap-3 px-2 py-2.5">{body}</div>
      )}
      {onDeleteRequest && canDeleteRecurringPayment(item) && (
        <Button
          type="button"
          variant="ghost-icon"
          size="icon-sm"
          onClick={requestDelete}
          aria-label={`Excluir ${name}`}
          title={`Excluir ${name}`}
          className="relative mr-1 shrink-0 self-start text-text-tertiary opacity-100 hover:bg-error-surface hover:text-error-text sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
        >
          <span
            className="absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2 pointer-fine:hidden"
            aria-hidden="true"
          />
          <TrashIcon size={15} weight="bold" />
        </Button>
      )}
    </li>
  );
}

/** The origin currency, called out on the name line so a foreign series is scannable in the list. */
function CurrencyBadge({ item }: { item: RecurringPayment }) {
  if (!item.price || item.price.currencyCode === 'BRL') return null;
  return (
    <span className={cn('shrink-0 rounded-full bg-surface-sunken px-1.5 py-px', 'font-mono text-3xs font-medium text-text-secondary')}>
      {item.price.currencyCode}
    </span>
  );
}

/**
 * How much of an installment plan is still owed — only meaningful while parcelas remain AND the
 * per-charge value exists in reais. Without a conversion `amount` is not a spendable figure, so
 * multiplying it would either print "faltam R$ 0,00" or label foreign money as BRL.
 */
function RemainingLabel({
  item,
  formatCurrency,
}: {
  item: RecurringPayment;
  formatCurrency: ReturnType<typeof useFormatCurrency>;
}) {
  const remaining = item.installmentRemaining ?? 0;
  if (remaining <= 0 || !hasConvertedAmount(item)) return null;
  return (
    <span className="hidden text-3xs text-text-tertiary sm:inline">
      faltam {formatCurrency(remaining * item.amount)}
    </span>
  );
}
